import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useLayoutEffect } from "react";
import { CryptoContext } from "../context/CryptoContext";
import Chart from "./Chart";

import { CiGlobe } from "react-icons/ci";
import { FaGithub, FaRedditAlien, FaFacebook, FaArrowCircleLeft  } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { IoDocumentTextOutline, IoChatbubbleSharp } from "react-icons/io5";

const HighLowIndicator = ({ currentPrice, high, low }) => {
  const [green, setGreen] = useState();

  useEffect(() => {
    let total = high - low;
    let greenZone = ((high - currentPrice) * 100) / total;
    setGreen(Math.ceil(greenZone));
  }, [currentPrice, high, low]);

  return (
    <>
      <span
        className="bg-red h-1.5 rounded-l-lg w-[50%]"
        style={{ width: `${100 - green}%` }}
      >
        &nbsp;
      </span>
      <span
        className="bg-green h-1.5 rounded-r-lg w-[50%]"
        style={{ width: `${green}%` }}
      >
        &nbsp;
      </span>
    </>
  );
};

const CryptoDetails = () => {
  let { coinId } = useParams();
  let navigate = useNavigate();
  let { getCoinData, coinData: data, currency } = useContext(CryptoContext);

  useLayoutEffect(() => {
    getCoinData(coinId);
  }, [coinId]);

  const handleBack = () => {
    navigate(-1)
  }

  return ReactDOM.createPortal(
    <div className="relative md:absolute top-30 w-full h-full backdrop-blur-sm font-nunito">
      <div
        className="w-[100%] h-full sm:w-full sm:px-4 md:px-20 py-10 text-white relative"
        onClick={(e) => e.stopPropagation()}
      >
        {data ? (
          <div className="flex flex-col md:flex-row items-center justify-between h-full w-full p-4 space-y-10 md:space-y-0 md:space-x-8">
            
            {/* Left Column */}
            <div className="flex flex-col w-full md:w-[45%] h-full">
            <button onClick={handleBack} className="w-[5%] text-lg mb-10" ><FaArrowCircleLeft /></button>
              <div className="flex w-full items-center">
                <img
                  className="w-8 h-8 mx-2"
                  src={data.image.large}
                  alt={data.id}
                />
                <h1 className="text-lg capitalize font-medium">{data.name}</h1>
                <span className="text-sm py-0.5 px-2.5 ml-2 bg-green text-green bg-opacity-25 rounded uppercase">
                  {data.symbol}
                </span>
              </div>

              <div className="flex w-full mt-6 justify-between">
                <h2
                  className="text-xxl font-bold"
                  style={{ letterSpacing: "2px" }}
                >
                  {new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: currency,
                    maximumSignificantDigits: 5,
                  }).format(data.market_data.current_price[currency])}
                </h2>

                <div className="flex items-center space-x-2">
                  <div
                    className={`text-md px-1 flex items-center rounded uppercase font-bold ${data.market_data.price_change_percentage_24h > 0
                      ? "text-green"
                      : "text-red"
                      }`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`w-[1rem] ml-0.5 ${data.market_data.price_change_percentage_24h > 0
                        ? "fill-green rotate-180"
                        : "fill-red"
                        }`}
                    >
                      <path d="M7.47951 11.4153C7.42599 11.493 7.35438 11.5565 7.27085 11.6004C7.18732 11.6444 7.09437 11.6673 7.00001 11.6673C6.90564 11.6673 6.81269 11.6444 6.72916 11.6004C6.64563 11.5565 6.57402 11.493 6.52051 11.4153L1.27051 3.83194C1.20974 3.74447 1.1741 3.64202 1.16747 3.53572C1.16084 3.42943 1.18346 3.32334 1.23289 3.229C1.28232 3.13466 1.35665 3.05567 1.44782 3.0006C1.53899 2.94554 1.6435 2.91652 1.75001 2.91669H12.25C12.3563 2.91713 12.4604 2.94652 12.5512 3.00172C12.642 3.05691 12.716 3.13581 12.7653 3.22993C12.8147 3.32406 12.8374 3.42984 12.8311 3.53591C12.8247 3.64199 12.7896 3.74433 12.7295 3.83194L7.47951 11.4153Z" />
                    </svg>
                    <span>
                      {Number(
                        data.market_data.price_change_percentage_24h
                      ).toFixed(2)}
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex w-full mt-4 justify-between">
                <span className="text-md capitalize text-lightgray">
                  Market Cap
                </span>
                <h2 className="text-base font-bold">
                  {new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: currency,
                    minimumFractionDigits: 0,
                  }).format(data.market_data.market_cap[currency])}
                </h2>
              </div>

              <div className="flex w-full mt-4 justify-between">
                <span className="text-md capitalize text-lightGray">
                  Total Volume
                </span>
                <h2 className="text-base font-bold">
                  {new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: currency,
                    minimumFractionDigits: 0,
                  }).format(data.market_data.total_volume[currency])}
                </h2>
              </div>

              <div className="flex w-full mt-4 justify-between">
                <span className="text-md capitalize text-lightGray">
                  Fully Diluted Valuation
                </span>
                <h2 className="text-base font-bold">
                  ${new Intl.NumberFormat("en-IN", {
                    // style: "currency",
                    // currency: currency,
                    // notation: "compact",
                  }).format(
                    data.market_data.fully_diluted_valuation['usd']
                  )}
                </h2>
              </div>

              <div className="flex w-full mt-4 justify-between">
                <HighLowIndicator
                  currentPrice={data.market_data.current_price[currency]}
                  high={data.market_data.high_24h[currency]}
                  low={data.market_data.low_24h[currency]}
                />
              </div>

              <div className="flex justify-between mt-5">
                <span className="text-md capitalize text-lightgray">Low 24H</span>
                <h2 className="text-base font-bold">
                  {new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: currency,
                    minimumFractionDigits: 5,
                  }).format(data.market_data.low_24h[currency])}
                </h2>
              </div>

              <div className="flex justify-between mt-5">
                <span className="text-md capitalize text-lightgray">
                  High 24H
                </span>
                <h2 className="text-base font-bold">
                  {new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: currency,
                    minimumFractionDigits: 5,
                  }).format(data.market_data.high_24h[currency])}
                </h2>
              </div>

              <div className="flex justify-between mt-5">
                <span className="text-md capitalize text-lightgray">
                  Max Supply
                </span>
                <h2 className="text-base font-bold uppercase">
                  {new Intl.NumberFormat().format(data.market_data.max_supply)}
                  &nbsp;{data.symbol}
                </h2>
              </div>
              <div className="flex justify-between mt-5">
                <span className="text-md capitalize text-lightgray">
                  Circulating Supply
                </span>
                <h2 className="text-base font-bold uppercase">
                  {new Intl.NumberFormat().format(
                    data.market_data.circulating_supply
                  )}
                  &nbsp;{data.symbol}
                </h2>
              </div>
              {/* official links */}
              <h1 className="font-bold mt-5">Official Links</h1>
              <div className="flex w-full mt-2 flex-wrap space-x-2">
                {data?.links?.homepage[0] && (<a
                  target={"_blank"}
                  rel="noreferrer"
                  className="flex items-center text-md font-bold bg-btnBg px-1.5 py-0.5 m-1 rounded-lg"
                  href={data?.links?.homepage[0]}
                >
                  <CiGlobe className="mr-1" />
                  Website
                </a>
                )}

                {data?.links?.official_forum_url[0] && (
                  <a
                    target={"_blank"}
                    rel="noreferrer"
                    className="flex items-center text-md font-bold bg-btnBg px-1.5 py-0.5 m-1 rounded-lg"
                    href={data?.links?.official_forum_url[0]}
                  >
                    <IoDocumentTextOutline className="mr-1" />
                    Forum
                  </a>
                )}

                {data?.links?.repos_url.github && (
                  <a
                    target={"_blank"}
                    rel="noreferrer"
                    className="flex items-center text-md font-bold bg-btnBg px-1.5 py-0.5 m-1 rounded-lg"
                    href={data?.links?.repos_url.github[0]}
                  >
                    <FaGithub className="mr-1" />
                    GitHub
                  </a>
                )}
              </div>

              {/* social link */}
              <h1 className="font-bold mt-5">Social Links</h1>
              <div className="flex w-full mt-2 flex-wrap space-x-2">

                {data?.links?.subreddit_url && (<a
                  target={"_blank"}
                  rel="noreferrer"
                  className="flex items-center text-md font-bold bg-btnBg px-1.5 py-0.5 m-1 rounded-lg"
                  href={data.links.subreddit_url}
                >
                  <FaRedditAlien className="mr-1" />
                  Reddit
                </a>
                )}

                {data?.links?.twitter_screen_name && (
                  <a
                    target={"_blank"}
                    rel="noreferrer"
                    className="flex items-center text-md font-bold bg-btnBg px-1.5 py-0.5 m-1 rounded-lg"
                    href={`https://x.com/${data.links.twitter_screen_name}`}
                  >
                    <FaXTwitter className="mr-1" />
                    Twitter
                  </a>
                )}

                {data?.links?.chat_url[0] && (
                  <a
                    target={"_blank"}
                    rel="noreferrer"
                    className="flex items-center text-md font-bold bg-btnBg px-1.5 py-0.5 m-1 rounded-lg"
                    href={data.links.chat_url[0]}
                  >
                    <IoChatbubbleSharp className="mr-1" />
                    Chat
                  </a>
                )}

                {data?.links?.facebook_username && (
                  <a
                    target={"_blank"}
                    rel="noreferrer"
                    className="flex items-center text-md font-bold bg-btnBg px-1.5 py-0.5 m-1 rounded-lg"
                    href={`https://www.facebook.com/${data.links.facebook_username}`}
                  >
                    <FaFacebook className="mr-1" />
                    Facebook
                  </a>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="flex flex-col w-full md:w-[50%] h-full mt-10 md:mt-0">
              <div className="w-full h-64 md:h-full">
                <Chart id={data.id} />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full min-h-[60vh] h-full flex justify-center items-center">
            <div
              className="w-8 h-8 border-4 border-green rounded-full
             border-b-gray-200 animate-spin"
              role="status"
            />
            <span className="ml-2">please wait...</span>
          </div>
        )}
      </div>
    </div>,
    document.getElementById("model")
  );
};

export default CryptoDetails;
